/*
 * EwKlaviyo (TN-02): the one client-side Klaviyo subscribe, on API revision 2026-07-15.
 * Used by EwOffers.claim() in Simple mode (the cart email field, the pop-up and the footer).
 *
 * subscribe({email, source, properties, timeoutMs}) → Promise<{ok, httpStatus, reason}>
 *   POST https://a.klaviyo.com/client/subscriptions?company_id=<window.theme.klaviyo.companyId>
 *   with an explicit email-marketing consent (without it Klaviyo defaults the consent) and
 *   the list `window.theme.klaviyo.listId`, which the endpoint requires. `ok` only on 202.
 *   A blank company or list id answers `reason: 'config'` without sending anything.
 *   `reason`: '' | 'config' | 'http' | 'timeout' | 'network'. Never rejects.
 * identify(email): fire-and-forget `klaviyo.identify` when the onsite script is present (in
 *   the EEA it may do nothing until consent is given).
 */
(function () {
  const REVISION = '2026-07-15';
  const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';

  function settings() {
    const klaviyo = (window.theme && window.theme.klaviyo) || {};
    return {
      companyId: String(klaviyo.companyId || '').trim(),
      listId: String(klaviyo.listId || '').trim(),
    };
  }

  function body(email, source, properties, listId) {
    const attributes = { email: String(email || '').trim() };
    if (properties && Object.keys(properties).length) {
      attributes.properties = properties;
    }
    attributes.subscriptions = {
      email: { marketing: { consent: 'SUBSCRIBED' } },
    };
    const data = {
      type: 'subscription',
      attributes: {
        profile: { data: { type: 'profile', attributes: attributes } },
      },
      relationships: { list: { data: { type: 'list', id: listId } } },
    };
    if (source) data.attributes.custom_source = source;
    return { data: data };
  }

  // The request as it would be sent, or null when the store's ids are not set.
  function request(options) {
    const ids = settings();
    if (!ids.companyId || !ids.listId) return null;
    return {
      url: ENDPOINT + '?company_id=' + encodeURIComponent(ids.companyId),
      headers: {
        revision: REVISION,
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
      },
      body: JSON.stringify(
        body(options.email, options.source, options.properties, ids.listId),
      ),
    };
  }

  function subscribe(options) {
    const opts = options || {};
    const req = request(opts);
    if (!req) {
      return Promise.resolve({ ok: false, httpStatus: 0, reason: 'config' });
    }
    const timeoutMs = opts.timeoutMs || 15000;
    const controller =
      typeof AbortController === 'function' ? new AbortController() : null;
    let timer = null;
    if (controller) {
      timer = setTimeout(function () {
        controller.abort();
      }, timeoutMs);
    }
    return fetch(req.url, {
      method: 'POST',
      headers: req.headers,
      body: req.body,
      credentials: 'omit',
      signal: controller ? controller.signal : undefined,
    })
      .then(
        function (response) {
          const ok = response.status === 202;
          return {
            ok: ok,
            httpStatus: response.status,
            reason: ok ? '' : 'http',
          };
        },
        function (error) {
          return {
            ok: false,
            httpStatus: 0,
            reason:
              error && error.name === 'AbortError' ? 'timeout' : 'network',
          };
        },
      )
      .then(function (result) {
        if (timer) clearTimeout(timer);
        return result;
      });
  }

  function identify(email) {
    try {
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify({ email: email });
      }
    } catch (error) {
      // Fire-and-forget.
    }
  }

  window.EwKlaviyo = {
    subscribe: subscribe,
    identify: identify,
    _test: { REVISION: REVISION, body: body, request: request },
  };
})();
